import { Field, FieldProps } from 'formik';
import React, { ReactNode } from 'react';
import { InjectedIntl, injectIntl } from 'react-intl';
import styled from 'styled-components';
import ErrorMessage from '../../components/errorMessage/ErrorMessage';
import { TranslationHelpers } from '../../../../i18n/translationHelpers';
import {
    TwoColFormLeftElement,
    TwoColFormRightElement,
    TwoColFormWrapper,
} from '../../components/FormLayoutStyles';
import { getFullYear, validateExpiration } from '../../validator/DobValidator';
import InputField from '../../components/InputField';
import PaymentOutterInput from '../../../../details/paymentMethods/cards/cardForm/payVaultFields/PaymentOutterInput';

interface IExpiryDateFieldProps {
    autoFocus?: boolean;
    intl: InjectedIntl;
    monthValue?: string;
    yearValue?: string;
    required?: boolean;
}

interface IExpiryDateFieldStates {
    monthFocus: boolean;
    yearFocus: boolean;
}
const TwoColFormDateWrapper = styled(TwoColFormWrapper)`
    margin-bottom: 0px;
    position: relative;
`;

class ExpiryDateField extends React.Component<IExpiryDateFieldProps, IExpiryDateFieldStates> {
    private readonly ID: string;
    private readonly LABEL_ID: string;
    private readonly ERROR_REQUIRED_ID: string;

    constructor(props: IExpiryDateFieldProps) {
        super(props);

        this.ID = 'expiryDate';
        this.LABEL_ID = 'label.expiry_date';
        this.ERROR_REQUIRED_ID = 'error.expiry_required';

        this.validate = this.validate.bind(this);
        this.renderInputField = this.renderInputField.bind(this);

        this.state = {
            monthFocus: false,
            yearFocus: false,
        };
    }

    public render() {
        return <Field name={this.ID} validate={this.validate} render={this.renderInputField} />;
    }

    private validate(): string {
        const { intl, monthValue, yearValue, required } = this.props;
        if (monthValue && monthValue.length < 2) {
            return intl.formatMessage(
                TranslationHelpers.getMessageDescriptor(this.ERROR_REQUIRED_ID),
            );
        }

        const fullYearValue = yearValue ? getFullYear(yearValue) : yearValue;

        let expiryDate;
        try {
            expiryDate = new Date(`${fullYearValue}-${monthValue}-01`);
        } catch (e) {
            return intl.formatMessage(
                TranslationHelpers.getMessageDescriptor(this.ERROR_REQUIRED_ID),
            );
        }

        if (required && (!monthValue || !fullYearValue)) {
            return intl.formatMessage(
                TranslationHelpers.getMessageDescriptor(this.ERROR_REQUIRED_ID),
            );
        }

        if (
            monthValue &&
            fullYearValue &&
            (expiryDate.toString() === 'Invalid Date' || !validateExpiration(expiryDate))
        ) {
            return intl.formatMessage(
                TranslationHelpers.getMessageDescriptor(this.ERROR_REQUIRED_ID),
            );
        }

        return '';
    }

    private renderInputField({
        field,
        form: { touched, errors, handleBlur, setFieldValue },
    }: FieldProps): ReactNode {
        const { monthValue, yearValue, required } = this.props;
        const isTouched = touched && (touched.expireMonth || touched.expireYear);
        const errorMessage = isTouched && errors && errors[this.ID] ? errors[this.ID] : undefined;

        const onBlurMonth = (event: React.FocusEvent<HTMLInputElement>) => {
            this.setState({
                monthFocus: false,
            });
            setFieldValue('expireMonth', event.target.value);
            handleBlur(event);
        };

        const onBlurYear = (event: React.FocusEvent<HTMLInputElement>) => {
            this.setState({
                yearFocus: false,
            });
            setFieldValue('expireYear', event.target.value);
            handleBlur(event);
        };
        const monthLabelClassName =
            !!errorMessage || this.state.monthFocus ? 'form-label-focus' : 'form-label';
        const yearLabelClassName =
            !!errorMessage || this.state.yearFocus ? 'form-label-focus' : 'form-label';

        return (
            <>
                <TwoColFormDateWrapper>
                    <TwoColFormLeftElement style={{ position: 'relative' }}>
                        <label
                            className={monthLabelClassName}
                            id={'exp_month_label'}
                            htmlFor="expireMonth"
                        >
                            {TranslationHelpers.getFormattedMessage('label.month_payment_edit')} *
                        </label>
                        <PaymentOutterInput
                            showError={!!errorMessage}
                            aria-labelledby={'exp_month_label'}
                            focus={this.state.monthFocus}
                        >
                            <InputField
                                autoComplete="cc-exp-month"
                                type="text"
                                id={'expireMonth'}
                                maxLength={2}
                                name={'expireMonth'}
                                value={monthValue}
                                required={required}
                                onBlur={onBlurMonth}
                                hiddenBorder={true}
                                onFocus={() => {
                                    this.setState({
                                        monthFocus: true,
                                    });
                                }}
                                className="wide"
                            />
                        </PaymentOutterInput>
                    </TwoColFormLeftElement>
                    <TwoColFormRightElement style={{ position: 'relative' }}>
                        <label
                            className={yearLabelClassName}
                            id={'exp_year_label'}
                            htmlFor="expireYear"
                        >
                            {TranslationHelpers.getFormattedMessage('label.year_payment_edit')} *
                        </label>
                        <PaymentOutterInput
                            showError={!!errorMessage}
                            aria-labelledby={'exp_year_label'}
                            focus={this.state.yearFocus}
                        >
                            <InputField
                                autoComplete="cc-exp-year"
                                type="text"
                                id={'expireYear'}
                                maxLength={2}
                                name={'expireYear'}
                                value={yearValue}
                                required={required}
                                onBlur={onBlurYear}
                                hiddenBorder={true}
                                onFocus={() => {
                                    this.setState({
                                        yearFocus: true,
                                    });
                                }}
                                className="wide"
                            />
                        </PaymentOutterInput>
                    </TwoColFormRightElement>
                </TwoColFormDateWrapper>
                {errorMessage && <ErrorMessage message={errorMessage} />}
            </>
        );
    }
}

export default injectIntl(ExpiryDateField);
